(function (window) {
  'use strict';

  var TENANT_KEYS = new Set(['company_id', 'companyId']);
  var SAFE_CODE = /^[A-Z][A-Z0-9_]{1,119}$/;
  var SAFE_REQUEST_ID = /^[A-Za-z0-9_-]{1,160}$/;
  var DEFAULT_TIMEOUT_MS = 20000;

  function isPlainObject(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    var prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null || prototype?.constructor?.name === 'Object';
  }

  function assertNoTenantFields(value, seen) {
    if (!value || typeof value !== 'object') return;
    seen = seen || new Set();
    if (seen.has(value)) throw new TypeError('Invalid circular browser payload');
    seen.add(value);
    if (Array.isArray(value)) {
      value.forEach(function (entry) { assertNoTenantFields(entry, seen); });
    } else {
      if (!isPlainObject(value)) throw new TypeError('Invalid browser payload');
      Object.keys(value).forEach(function (key) {
        if (TENANT_KEYS.has(key)) throw new TypeError('Browser tenant fields are forbidden');
        assertNoTenantFields(value[key], seen);
      });
    }
    seen.delete(value);
  }

  function safeFieldErrors(value) {
    if (!isPlainObject(value)) return {};
    var result = {};
    Object.keys(value).slice(0, 100).forEach(function (key) {
      if (!/^[A-Za-z0-9_.-]{1,160}$/.test(key)) return;
      var messages = Array.isArray(value[key]) ? value[key] : [];
      result[key] = messages.slice(0, 20).map(function (entry) {
        return typeof entry === 'string' && entry.length <= 500 ? entry : 'invalid';
      });
    });
    return result;
  }

  function PortalApiError(payload, status) {
    var code = typeof payload?.code === 'string' && SAFE_CODE.test(payload.code)
      ? payload.code : status === 401 ? 'AUTHENTICATION_REQUIRED' : 'REQUEST_FAILED';
    var error = new Error('portal_request_failed');
    error.name = 'PortalApiError';
    error.status = Number.isSafeInteger(status) ? status : 0;
    error.code = code;
    error.field_errors = safeFieldErrors(payload?.field_errors);
    error.request_id = typeof payload?.request_id === 'string' && SAFE_REQUEST_ID.test(payload.request_id)
      ? payload.request_id : '';
    error.refresh_capabilities = payload?.refresh_capabilities === true || (status === 403 && code === 'ACTION_FORBIDDEN');
    return error;
  }

  function sanitizeSignedUrl(value) {
    if (typeof value !== 'string' || !value || value.length > 16384) return '';
    try {
      var parsed = new URL(value);
      if (parsed.username || parsed.password || parsed.hash) return '';
      if (parsed.protocol === 'https:') return parsed.href;
      if (parsed.protocol === 'http:' && ['127.0.0.1', 'localhost', '[::1]'].includes(parsed.hostname)) return parsed.href;
    } catch (error) {
      return '';
    }
    return '';
  }

  function joinUrl(baseUrl, path) {
    var base = String(baseUrl || '').replace(/\/+$/, '');
    var suffix = '/' + String(path || '').replace(/^\/+/, '');
    if (!base || base.includes('?') || base.includes('#') || /[\u0000-\u001f\u007f]/.test(base)) {
      throw new TypeError('Invalid portal API base URL');
    }
    return base + suffix;
  }

  function appProxyBaseUrl(value) {
    var base = String(value || '').replace(/\/+$/, '');
    // Identity comes from Shopify's signed proxy request, never a backend session cookie.
    if (!/^\/apps\/[A-Za-z0-9_-]+$/.test(base)) {
      throw new TypeError('A same-origin Shopify App Proxy URL is required');
    }
    return base;
  }

  function parseJson(text) {
    if (!text) return null;
    try {
      var value = JSON.parse(text);
      return isPlainObject(value) ? value : null;
    } catch (error) {
      return null;
    }
  }

  function createBoundSignal(owner, externalSignal, timeoutMs) {
    var controller = new AbortController();
    owner.add(controller);
    var onAbort = function () { controller.abort(externalSignal?.reason); };
    if (externalSignal) {
      if (externalSignal.aborted) onAbort();
      else externalSignal.addEventListener('abort', onAbort, { once: true });
    }
    var timeout = setTimeout(function () {
      controller.abort(new DOMException('Portal request timed out', 'TimeoutError'));
    }, timeoutMs);
    return {
      signal: controller.signal,
      release: function () {
        clearTimeout(timeout);
        owner.delete(controller);
        externalSignal?.removeEventListener?.('abort', onAbort);
      },
    };
  }

  function PortalApi(options) {
    options = options || {};
    this.baseUrl = appProxyBaseUrl(options.baseUrl);
    this.fetch = options.fetchImpl || (typeof fetch === 'function' ? fetch.bind(window) : null);
    this.timeoutMs = Number.isSafeInteger(options.timeoutMs) && options.timeoutMs >= 1000
      ? options.timeoutMs : DEFAULT_TIMEOUT_MS;
    this.controllers = new Set();
    this.intent = options.intent || window.TodayMerchClientMutationIntent;
    this.onUnauthorized = typeof options.onUnauthorized === 'function' ? options.onUnauthorized : null;
    this.getAccessToken = typeof options.getAccessToken === 'function' ? options.getAccessToken : null;
    this.invalidateAccessToken = typeof options.invalidateAccessToken === 'function' ? options.invalidateAccessToken : null;
    this.locked = false;
    // Private, instance-local reads only. No browser storage or HTTP cache is used.
    this.readCache = new Map();
    this.readInflight = new Map();
    this.cacheScope = '';
    this.cacheGeneration = 0;
    this.cacheListeners = new Set();
    this.cacheNow = typeof options.now === 'function' ? options.now : Date.now;
    if (typeof this.fetch !== 'function') throw new TypeError('Portal fetch is unavailable');
  }

  PortalApi.prototype.assertUnlocked = function () {
    if (this.locked) throw PortalApiError({ code: 'AUTHENTICATION_REQUIRED' }, 401);
  };

  PortalApi.prototype.abortAll = function () {
    this.controllers.forEach(function (controller) { controller.abort(); });
    this.controllers.clear();
  };

  PortalApi.prototype.addCacheInvalidationListener = function (listener) {
    this.cacheListeners.add(listener);
    return function () { this.cacheListeners.delete(listener); }.bind(this);
  };

  PortalApi.prototype.invalidateCache = function (prefixes) {
    var paths = Array.isArray(prefixes) && prefixes.length ? prefixes : null;
    this.cacheGeneration++;
    function matches(path) { return !paths || paths.some(function (prefix) { return path === prefix || path.startsWith(prefix + '/') || path.startsWith(prefix + '?'); }); }
    this.readCache.forEach(function (entry, key) { if (matches(entry.path)) this.readCache.delete(key); }, this);
    this.readInflight.forEach(function (entry, key) { if (matches(entry.path)) this.readInflight.delete(key); }, this);
    this.cacheListeners.forEach(function (listener) { listener(paths); });
  };

  PortalApi.prototype.clearCache = function () { this.invalidateCache(); };

  PortalApi.prototype.setCacheScope = function (scope) {
    var next = typeof scope === 'string' ? scope : JSON.stringify(scope || null);
    if (next === this.cacheScope) return;
    this.clearCache();
    this.abortAll();
    this.cacheScope = next;
  };

  function copyRead(value) { return typeof structuredClone === 'function' ? structuredClone(value) : JSON.parse(JSON.stringify(value)); }
  function cacheableRead(path, options, api) {
    return api.cacheScope && String(options.method || 'GET').toUpperCase() === 'GET'
      && !options.signal && !options.headers && options.body === undefined && options.cache !== 'no-store'
      && /^\/client\/v1\/(?:orders(?:\/[0-9a-f-]{36})?|account(?:\/(?:invoices|inventory))?|inventory)\/?(?:\?|$)/i.test(path);
  }

  PortalApi.prototype.lock = function () {
    if (this.locked) return;
    this.locked = true;
    this.clearCache();
    this.abortAll();
    try { this.onUnauthorized?.(); } catch (error) { /* the lock remains authoritative */ }
  };

  PortalApi.prototype.validateCachedAccess = async function () {
    if (!this.getAccessToken) return true;
    if (this.authValidation) return this.authValidation;
    var token;
    try { token = safeBearerToken(await this.getAccessToken()); }
    catch (error) { this.lock(); throw PortalApiError({ code: 'AUTHENTICATION_REQUIRED' }, 401); }
    if (this.authValidation) return this.authValidation;
    if (this.cacheAuthToken !== undefined && this.cacheAuthToken !== token) {
      if (this.cacheAuthToken && !token) { this.lock(); throw PortalApiError({ code: 'AUTHENTICATION_REQUIRED' }, 401); }
      var self = this;
      var validation = Promise.resolve().then(function () {
        return typeof self.validateCacheIdentity === 'function' ? self.validateCacheIdentity() : false;
      }).then(function (sameIdentity) {
        if (sameIdentity !== true) { self.clearCache(); self.cacheScope = ''; }
        self.cacheAuthToken = token;
        return sameIdentity === true;
      }).catch(function (error) { self.clearCache(); self.cacheScope = ''; throw error; })
        .finally(function () { if (self.authValidation === validation) self.authValidation = null; });
      this.authValidation = validation;
      return validation;
    }
    this.cacheAuthToken = token;
    return true;
  };

  PortalApi.prototype.setAccessTokenProvider = function (provider, invalidator) {
    this.getAccessToken = typeof provider === 'function' ? provider : null;
    this.invalidateAccessToken = typeof invalidator === 'function' ? invalidator : null;
  };

  PortalApi.prototype.invalidateAuth = function () {
    try { this.invalidateAccessToken?.(); } catch (error) { /* locking remains authoritative */ }
  };

  function safeBearerToken(value) {
    if (typeof value !== 'string') return '';
    var token = value.trim();
    if (!token || token.length > 8192 || /[\s\u0000-\u001f\u007f]/.test(token)) return '';
    return token;
  }

  PortalApi.prototype.resolveAuthHeaders = async function (headers) {
    if (!this.getAccessToken || Object.keys(headers).some(function (key) { return key.toLowerCase() === 'authorization'; })) return headers;
    var token = '';
    try { token = safeBearerToken(await this.getAccessToken()); }
    catch (error) {
      this.lock();
      throw PortalApiError({ code: 'AUTHENTICATION_REQUIRED' }, 401);
    }
    return token ? Object.assign({}, headers, { Authorization: 'Bearer ' + token }) : headers;
  };

  PortalApi.prototype.requestUncached = async function (path, options) {
    this.assertUnlocked();
    options = options || {};
    var method = String(options.method || 'GET').toUpperCase();
    var headers = Object.assign({ Accept: 'application/json' }, options.headers || {});
    var body;
    if (options.body !== undefined) {
      assertNoTenantFields(options.body);
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(options.body);
    }
    var bounded = createBoundSignal(this.controllers, options.signal, options.timeoutMs || this.timeoutMs);
    try {
      headers = await this.resolveAuthHeaders(headers);
      var response = await this.fetch(joinUrl(this.baseUrl, path), {
        method: method,
        headers: headers,
        body: body,
        credentials: 'include',
        cache: 'no-store',
        signal: bounded.signal,
      });
      var payload = parseJson(await response.text());
      if (!response.ok) {
        var responseError = PortalApiError(payload, response.status);
        if (response.status === 401) { this.invalidateAuth(); this.lock(); }
        else if (response.status === 403 || response.status === 423) this.clearCache();
        throw responseError;
      }
      if (payload === null) {
        if (response.status === 204) return {};
        throw PortalApiError({ code: 'PORTAL_RESPONSE_INVALID' }, 503);
      }
      if (/^\/client\/v1\/dashboard(?:\?|$)/.test(path) && this.getAccessToken) this.cacheAuthToken = safeBearerToken(String(headers.Authorization || '').replace(/^Bearer /, ''));
      return payload;
    } finally {
      bounded.release();
    }
  };

  PortalApi.prototype.request = async function (path, options) {
    this.assertUnlocked();
    options = options || {};
    if (!cacheableRead(path, options, this)) return this.requestUncached(path, options);
    if (this.getAccessToken && !await this.validateCachedAccess()) { this.clearCache(); this.cacheScope = ''; throw PortalApiError({ code: 'AUTH_CONTEXT_CHANGED' }, 401); }
    var key = this.cacheScope + ':' + path, entry = this.readCache.get(key);
    if (options.cache !== 'reload' && entry && this.cacheNow() - entry.time < 30000) {
      this.readCache.delete(key); this.readCache.set(key, entry);
      return copyRead(entry.value);
    }
    var active = this.readInflight.get(key);
    if (active) return copyRead(await active.promise);
    var scope = this.cacheScope, generation = this.cacheGeneration, self = this;
    var promise = this.requestUncached(path, options).then(function (value) {
      if (self.locked || self.cacheScope !== scope) throw PortalApiError({ code: 'AUTH_CONTEXT_CHANGED' }, 401);
      if (self.cacheGeneration === generation) {
        self.readCache.delete(key);
        self.readCache.set(key, { path: path, value: copyRead(value), time: self.cacheNow() });
        while (self.readCache.size > 30) self.readCache.delete(self.readCache.keys().next().value);
      }
      return value;
    }).finally(function () { if (self.readInflight.get(key)?.promise === promise) self.readInflight.delete(key); });
    this.readInflight.set(key, { path: path, promise: promise });
    return copyRead(await promise);
  };

  PortalApi.prototype.invalidateMutation = function (path) {
    if (/^\/client\/v1\/(?:beneficiaries|beneficiary-(?:groups|imports|connections|audiences))(?:\/|$)/.test(String(path))) {
      this.invalidateCache(['/client/v1/beneficiaries', '/client/v1/beneficiary-groups', '/client/v1/beneficiary-imports', '/client/v1/beneficiary-connections', '/client/v1/beneficiary-audiences']);
      return;
    }
    var root = String(path).match(/^\/client\/v1\/(account|orders|inventory|shipments|projects|proofs|documents)(?:\/|$)/)?.[1];
    var prefixes = root === 'account' ? ['/client/v1/account']
      : root === 'inventory' || root === 'shipments' ? ['/client/v1/inventory', '/client/v1/account/inventory', '/client/v1/orders']
        : root ? ['/client/v1/orders', '/client/v1/account/invoices'] : null;
    this.invalidateCache(prefixes);
  };

  PortalApi.prototype.mutate = async function (path, options) {
    this.assertUnlocked();
    options = options || {};
    var expectedVersion = options.expectedVersion;
    if (!Number.isSafeInteger(expectedVersion) || expectedVersion < 0) {
      throw new TypeError('A canonical expected version is required');
    }
    var payload = options.body === undefined ? {} : options.body;
    if (!isPlainObject(payload)) throw new TypeError('A mutation body is required');
    assertNoTenantFields(payload);
    if (!this.intent || typeof this.intent.get !== 'function' || typeof this.intent.withContract !== 'function'
      || typeof this.intent.clear !== 'function') throw new Error('Secure mutation intent persistence is unavailable');
    var intent = await this.intent.get({
      operation: options.operation,
      resource: options.resource,
      payload: payload,
    });
    var contract = this.intent.withContract(payload, intent, expectedVersion);
    var result = await this.request(path, {
      method: options.method || 'POST',
      body: contract.body,
      headers: Object.assign({}, options.headers, contract.headers),
      signal: options.signal,
      timeoutMs: options.timeoutMs,
    });
    this.intent.clear(options.operation, intent);
    this.invalidateMutation(path);
    return result;
  };

  PortalApi.prototype.listAll = async function (path, options) {
    options = options || {};
    var limit = Number.isSafeInteger(options.limit) && options.limit > 0 && options.limit <= 100 ? options.limit : 50;
    var maximumPages = Number.isSafeInteger(options.maximumPages) && options.maximumPages > 0 && options.maximumPages <= 20
      ? options.maximumPages : 10;
    var cursor = null;
    var items = [];
    for (var page = 0; page < maximumPages; page += 1) {
      var separator = String(path).includes('?') ? '&' : '?';
      var query = separator + 'limit=' + limit + (cursor ? '&cursor=' + encodeURIComponent(cursor) : '');
      var result = await this.request(path + query, { signal: options.signal });
      if (!Array.isArray(result.items) || !(result.next_cursor === null || typeof result.next_cursor === 'string')) {
        throw PortalApiError({ code: 'PORTAL_RESPONSE_INVALID' }, 503);
      }
      items = items.concat(result.items);
      cursor = result.next_cursor;
      if (!cursor) return { items: items, next_cursor: null };
    }
    return { items: items, next_cursor: cursor };
  };

  PortalApi.prototype.putSigned = async function (signedUrl, file, options) {
    this.assertUnlocked();
    options = options || {};
    var url = sanitizeSignedUrl(signedUrl);
    if (!url) throw new TypeError('Invalid signed upload URL');
    var bounded = createBoundSignal(this.controllers, options.signal, options.timeoutMs || this.timeoutMs);
    try {
      var response = await this.fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': options.contentType || file?.type || 'application/octet-stream' },
        body: file,
        credentials: 'omit',
        cache: 'no-store',
        signal: bounded.signal,
      });
      if (!response.ok) {
        var uploadError = PortalApiError({ code: 'PRIVATE_UPLOAD_FAILED' }, response.status);
        if (response.status === 401) this.lock();
        throw uploadError;
      }
      return true;
    } finally {
      bounded.release();
    }
  };

  function consumeInvitationFragment(scope) {
    scope = scope || window;
    var raw = String(scope.location?.hash || '').replace(/^#/, '');
    if (!raw) return '';
    var params = new URLSearchParams(raw);
    var token = String(params.get('invite') || '');
    if (!token) return '';
    params.delete('invite');
    var fragment = params.toString();
    scope.history?.replaceState?.(null, '', String(scope.location.pathname || '/account')
      + String(scope.location.search || '') + (fragment ? '#' + fragment : ''));
    return /^[A-Za-z0-9_-]{8,512}$/.test(token) ? token : '';
  }

  window.TodayMerchPortalApi = Object.freeze({
    PortalApi: PortalApi,
    PortalApiError: PortalApiError,
    assertNoTenantFields: assertNoTenantFields,
    consumeInvitationFragment: consumeInvitationFragment,
    sanitizeSignedUrl: sanitizeSignedUrl,
  });
})(window);
